import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Sparkles, Bot } from 'lucide-react';
import UZBEK_NAMES from '../constants/uzbekNames';

function TypingDots() {
  return (
    <div className="dot-pulse">
      <span /><span /><span />
    </div>
  );
}

function Message({ msg, color }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`ai-msg-row ${isUser ? 'user' : 'ai'}`}>
      {!isUser && (
        <div className="ai-avatar" style={{ background: color + '22', color }}>
          <Bot size={14} />
        </div>
      )}
      <div className="ai-bubble" style={isUser ? { background: color, color: '#fff' } : {}}>
        {msg.content}
      </div>
    </div>
  );
}

export default function AiChat({ categoryName, color = '#378ADD', displayName: displayNameProp }) {
  const displayName = displayNameProp || UZBEK_NAMES[categoryName] || categoryName;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [welcomed, setWelcomed] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && !welcomed) {
      setMessages([{
        role: 'assistant',
        content: `Salom! Men sizga YouTube va boshqa har qanday savollarda yordam bera olaman. Nima qiziqtiradi?`,
      }]);
      setWelcomed(true);
    }
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open, welcomed, categoryName]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');

    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const res = await fetch(
        'https://youtube-analytics-backend-production.up.railway.app/api/chat',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages: [...messages, userMsg].filter(m => m.role !== 'system').map(msg => ({
              role: msg.role,
              content: msg.content
            })),
            categoryName: displayName
          })
        }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error?.message || `API xatosi: ${res.status}`);
      }

      const data = await res.json();
      const aiReply = data.reply || data.error || 'Javob olishda xatolik yuz berdi.';
      setMessages(prev => [...prev, { role: 'assistant', content: aiReply }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Xatolik yuz berdi: ${err.message}`,
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <>
      {/* Floating toggle button */}
      <button
        className="ai-toggle-btn"
        style={{ background: color }}
        onClick={() => setOpen(o => !o)}
        aria-label="AI maslahat"
      >
        {open ? <X size={22} /> : <Sparkles size={22} />}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="ai-panel">
          {/* Header */}
          <div className="ai-panel-header" style={{ background: color }}>
            <div className="ai-header-left">
              <Bot size={18} />
              <span>AI Maslahat</span>
              <span className="ai-badge">{displayName}</span>
            </div>
            <button className="ai-close-btn" onClick={() => setOpen(false)}>
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <div className="ai-messages">
            {messages.map((m, i) => (
              <Message key={i} msg={m} color={color} />
            ))}
            {loading && (
              <div className="ai-msg-row ai">
                <div className="ai-avatar" style={{ background: color + '22', color }}>
                  <Bot size={14} />
                </div>
                <div className="ai-bubble"><TypingDots /></div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="ai-input-row">
            <textarea
              ref={inputRef}
              className="ai-input"
              placeholder="Savolingizni yozing..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              rows={1}
              disabled={loading}
            />
            <button
              className="ai-send-btn"
              style={{ background: color }}
              onClick={send}
              disabled={!input.trim() || loading}
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
